/**
 * Settings screen — /app/settings. Profile (rename self) and, for owners,
 * Team management sit above the pre-existing Appearance/Outreach cards.
 * Mirrors the shell conventions of FeedPage/PipelinePage: same nav strip,
 * same card grammar.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OutreachSettingsResponse } from '@sourcito/schemas';
import type { TeamMember } from '@sourcito/schemas';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useTheme,
} from '../../design';
import { apiFetch, ApiCallError } from '../../lib/api';
import { AppShell } from '../../components/AppShell';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import { useMe, useUpdateMe } from '../../lib/auth';
import {
  useCreateTeamMember,
  useDeleteTeamMember,
  useTeam,
  useUpdateTeamMember,
  VA_PERMISSIONS,
} from '../../lib/team';
import {
  isFutureDate,
  isValidMarginFraction,
  marginFractionToPercent,
  marginPercentInputToFraction,
  useStoreSettings,
  useUpdateStoreSettings,
} from '../../lib/storeSettings';

const SETTINGS_KEY = ['outreach-settings'];

export function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiFetch('/api/outreach-settings', { responseSchema: OutreachSettingsResponse }),
  });

  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);

  // Hydrate the textarea once the fetch lands; later refetches must not
  // clobber in-progress edits, so only sync while the field is untouched.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched && data) setDraft(data.instructions ?? '');
  }, [data, touched]);

  const save = useMutation({
    mutationFn: (instructions: string) =>
      apiFetch('/api/outreach-settings', {
        method: 'PUT',
        body: JSON.stringify({ instructions }),
        responseSchema: OutreachSettingsResponse,
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(SETTINGS_KEY, res);
      setTouched(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  return (
    <AppShell>
      <main className="max-w-3xl mx-auto px-8 py-8 space-y-6">
        <ProfileCard />

        <TeamCard />

        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>Theme for this browser.</CardDescription>
          </CardHeader>

          <div className="inline-flex gap-1 bg-surface-elev border border-border-subtle rounded-full p-1">
            <button
              type="button"
              onClick={() => setTheme('warm-light')}
              className={
                theme === 'warm-light'
                  ? 'bg-anchor text-canvas rounded-full px-3.5 py-1.5 text-sm'
                  : 'text-fg-secondary hover:text-fg-primary rounded-full px-3.5 py-1.5 text-sm'
              }
            >
              Light
            </button>
            <button
              type="button"
              onClick={() => setTheme('warm-dark')}
              className={
                theme === 'warm-dark'
                  ? 'bg-anchor text-canvas rounded-full px-3.5 py-1.5 text-sm'
                  : 'text-fg-secondary hover:text-fg-primary rounded-full px-3.5 py-1.5 text-sm'
              }
            >
              Dark
            </button>
          </div>

          {theme === 'warm-dark' && (
            <p className="text-xs text-fg-tertiary mt-2">
              The dark theme predates the redesign — a retune is coming.
            </p>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Outreach instructions</CardTitle>
            <CardDescription>
              Standing guidance applied to every email draft this workspace generates. Example:
              &ldquo;Mention that the store is owned by minority women.&rdquo;
            </CardDescription>
          </CardHeader>

          <textarea
            value={draft}
            onChange={(e) => {
              setTouched(true);
              setDraft(e.target.value);
            }}
            disabled={isLoading}
            rows={5}
            maxLength={2000}
            placeholder="e.g. Mention that the store is owned by minority women. Prefer a warm, direct tone."
            className="w-full text-sm rounded-sm border border-border-subtle bg-canvas px-3 py-2 focus:outline-none focus:ring-1 focus:ring-accent-fg"
          />

          <div className="flex items-center gap-3 mt-3">
            <Button
              size="sm"
              onClick={() => save.mutate(draft)}
              loading={save.isPending}
              disabled={isLoading || save.isPending}
            >
              {saved ? 'Saved' : 'Save'}
            </Button>
            <p className="text-xs text-fg-tertiary">
              Leave empty to clear. Applies to new drafts only.
            </p>
          </div>
          {save.isError && (
            <p role="alert" className="text-xs text-negative mt-2">
              {(save.error as Error)?.message ?? 'Save failed'}
            </p>
          )}
        </Card>

        <StoreSettingsCard />

        <PricingTargetsCard />
      </main>
    </AppShell>
  );
}

// ─────────────────────────────────────────────────────────
// Store settings — the store birthday, floors Amazon report backfill
// windows so a store that started in 2026 never gets synced back into 2025
// history that doesn't exist. See lib/storeSettings.ts and apps/api/src/
// routes/store-settings.ts. Same fetch/save/touched-hydrate pattern as the
// Outreach instructions card above.
// ─────────────────────────────────────────────────────────

function StoreSettingsCard() {
  const { data, isLoading } = useStoreSettings();
  const update = useUpdateStoreSettings();

  const [draft, setDraft] = useState('');
  const [touched, setTouched] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!touched && data) {
      setDraft(data.storeStartedAt ? data.storeStartedAt.slice(0, 10) : '');
    }
  }, [data, touched]);

  const invalid = isFutureDate(draft);

  async function handleSave() {
    if (invalid) return;
    try {
      await update.mutateAsync({
        storeStartedAt: draft ? new Date(draft).toISOString() : null,
      });
      setTouched(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // update.isError renders the message below.
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Store start date</CardTitle>
        <CardDescription>We won&rsquo;t look for Amazon history before this date.</CardDescription>
      </CardHeader>

      <div className="flex items-center gap-3">
        <Input
          inputSize="sm"
          type="date"
          value={draft}
          onChange={(e) => {
            setTouched(true);
            setDraft(e.target.value);
          }}
          disabled={isLoading}
          max={new Date().toISOString().slice(0, 10)}
          className="max-w-xs"
        />
        <Button
          size="sm"
          onClick={handleSave}
          loading={update.isPending}
          disabled={isLoading || update.isPending || invalid}
        >
          {saved ? 'Saved' : 'Save'}
        </Button>
      </div>
      <p className="text-xs text-fg-tertiary mt-2">Leave empty to clear (no floor).</p>
      {invalid && (
        <p role="alert" className="text-xs text-negative mt-2">
          Store start date cannot be in the future.
        </p>
      )}
      {update.isError && (
        <p role="alert" className="text-xs text-negative mt-2">
          {(update.error as Error)?.message ?? 'Save failed'}
        </p>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// Pricing targets — price calibration's min/target margin (COCKPIT-VISION.md
// build-order item 7, 2026-08-25). A sibling card rather than folding into
// Store settings above: unrelated concerns (backfill floor vs. margin
// targets) that happen to share one PUT endpoint at the API layer only.
// Displays/edits PERCENTS; the API stores/returns FRACTIONS — converted at
// the edge via lib/storeSettings.ts's pure helpers (unit-tested there).
// ─────────────────────────────────────────────────────────

function PricingTargetsCard() {
  const { data, isLoading } = useStoreSettings();
  const update = useUpdateStoreSettings();

  const [minDraft, setMinDraft] = useState('');
  const [targetDraft, setTargetDraft] = useState('');
  const [touched, setTouched] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!touched && data) {
      setMinDraft(String(marginFractionToPercent(data.minMarginPct)));
      setTargetDraft(String(marginFractionToPercent(data.targetMarginPct)));
    }
  }, [data, touched]);

  const minFraction = marginPercentInputToFraction(minDraft);
  const targetFraction = marginPercentInputToFraction(targetDraft);
  const minValid = isValidMarginFraction(minFraction);
  const targetValid = isValidMarginFraction(targetFraction);
  const orderValid =
    minFraction !== null && targetFraction !== null && minFraction <= targetFraction;
  const canSave = minValid && targetValid && orderValid;

  async function handleSave() {
    if (!canSave || minFraction === null || targetFraction === null) return;
    try {
      await update.mutateAsync({ minMarginPct: minFraction, targetMarginPct: targetFraction });
      setTouched(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // update.isError renders the message below.
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing targets</CardTitle>
        <CardDescription>
          The margin floor and target Price Calibration uses to suggest minimum and target prices
          for every product.
        </CardDescription>
      </CardHeader>

      <div className="flex items-end gap-3 flex-wrap">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.1em] text-fg-tertiary">
            Minimum margin
          </span>
          <div className="mt-1 flex items-center gap-1">
            <Input
              inputSize="sm"
              type="number"
              min={0}
              max={99}
              step={0.5}
              value={minDraft}
              onChange={(e) => {
                setTouched(true);
                setMinDraft(e.target.value);
              }}
              disabled={isLoading}
              className="max-w-[90px]"
            />
            <span className="text-sm text-fg-tertiary">%</span>
          </div>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.1em] text-fg-tertiary">
            Target margin
          </span>
          <div className="mt-1 flex items-center gap-1">
            <Input
              inputSize="sm"
              type="number"
              min={0}
              max={99}
              step={0.5}
              value={targetDraft}
              onChange={(e) => {
                setTouched(true);
                setTargetDraft(e.target.value);
              }}
              disabled={isLoading}
              className="max-w-[90px]"
            />
            <span className="text-sm text-fg-tertiary">%</span>
          </div>
        </label>
        <Button size="sm" onClick={handleSave} loading={update.isPending} disabled={!canSave}>
          {saved ? 'Saved' : 'Save'}
        </Button>
      </div>

      {!minValid && minDraft && (
        <p role="alert" className="text-xs text-negative mt-2">
          Minimum margin must be between 0% and 99%.
        </p>
      )}
      {!targetValid && targetDraft && (
        <p role="alert" className="text-xs text-negative mt-2">
          Target margin must be between 0% and 99%.
        </p>
      )}
      {minValid && targetValid && !orderValid && (
        <p role="alert" className="text-xs text-negative mt-2">
          Minimum margin cannot exceed the target margin.
        </p>
      )}
      {update.isError && (
        <p role="alert" className="text-xs text-negative mt-2">
          {(update.error as Error)?.message ?? 'Save failed'}
        </p>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// Profile — rename self (any authenticated role that's allowed to, per the
// API's own gate: not warehouse, not an unprovisioned va).
// ─────────────────────────────────────────────────────────

function ProfileCard() {
  const { data: me } = useMe();
  const updateMe = useUpdateMe();

  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    if (!touched && me) setName(me.user.name ?? '');
  }, [me, touched]);

  async function handleSave() {
    setStatus('idle');
    setErrorText(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setStatus('error');
      setErrorText('Name is required.');
      return;
    }
    try {
      await updateMe.mutateAsync({ name: trimmed });
      setTouched(false);
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (err) {
      setStatus('error');
      setErrorText(err instanceof ApiCallError ? err.message : 'Could not save.');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Your name, shown in the sidebar greeting.</CardDescription>
      </CardHeader>

      <div className="flex items-center gap-3">
        <Input
          inputSize="sm"
          value={name}
          onChange={(e) => {
            setTouched(true);
            setName(e.target.value);
          }}
          maxLength={80}
          placeholder="Your name"
          className="max-w-xs"
        />
        <Button
          size="sm"
          onClick={handleSave}
          loading={updateMe.isPending}
          disabled={updateMe.isPending}
        >
          Save
        </Button>
      </div>
      {status === 'saved' && <p className="text-xs text-positive mt-2">Saved</p>}
      {status === 'error' && errorText && (
        <p role="alert" className="text-xs text-negative mt-2">
          {errorText}
        </p>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// Team — owner-only VA provisioning + lifecycle. See lib/team.ts and
// apps/api/src/routes/team.ts.
// ─────────────────────────────────────────────────────────

function TeamCard() {
  const { data: me } = useMe();
  const isOwner = me?.user.role === 'owner';
  const teamQuery = useTeam(isOwner);

  if (!isOwner) return null;

  const members = teamQuery.data ?? [];

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="space-y-1">
          <CardTitle>Team</CardTitle>
          <CardDescription>
            Owners, operators, and VAs with access to this workspace.
          </CardDescription>
        </div>
        <AddVaDialog />
      </div>

      {teamQuery.isLoading && <TeamCardSkeleton />}
      {teamQuery.isError && <p className="text-sm text-negative">Could not load the team.</p>}
      {!teamQuery.isLoading && !teamQuery.isError && (
        <div className="divide-y divide-border-subtle">
          {members.map((member) => (
            <TeamMemberRow key={member.id} member={member} />
          ))}
        </div>
      )}
    </Card>
  );
}

/** Shimmer placeholder for TeamCard's member list while the team query is
 *  loading — mirrors TeamMemberRow's name/email + role-badge shape. */
function TeamCardSkeleton() {
  return (
    <div className="divide-y divide-border-subtle">
      {Array.from({ length: 3 }, (_, i) => (
        <div
          key={i}
          className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-3 flex-wrap"
          aria-hidden="true"
        >
          <div className="min-w-0 space-y-1.5">
            <SkeletonLine className="w-32" />
            <SkeletonLine className="w-44" />
          </div>
          <Skeleton className="h-5 w-16 rounded-sm shrink-0" />
        </div>
      ))}
    </div>
  );
}

const ROLE_BADGE_VARIANT: Record<TeamMember['role'], 'accent' | 'neutral' | 'info'> = {
  owner: 'accent',
  operator: 'neutral',
  va: 'info',
  warehouse: 'neutral',
};

function TeamMemberRow({ member }: { member: TeamMember }) {
  const updateMember = useUpdateTeamMember();
  const deleteMember = useDeleteTeamMember();
  const [error, setError] = useState<string | null>(null);
  const isVa = member.role === 'va';

  async function togglePermission(key: string) {
    setError(null);
    const has = member.permissions.includes(key);
    const nextPermissions = has
      ? member.permissions.filter((p) => p !== key)
      : [...member.permissions, key];
    try {
      await updateMember.mutateAsync({ id: member.id, permissions: nextPermissions });
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not update permissions.');
    }
  }

  async function handleRemove() {
    if (!confirm('They lose access immediately.')) return;
    setError(null);
    try {
      await deleteMember.mutateAsync(member.id);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not remove this person.');
    }
  }

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-medium text-fg-primary truncate">
            {member.name ?? member.email}
          </p>
          <p className="text-xs text-fg-tertiary truncate">{member.email}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={ROLE_BADGE_VARIANT[member.role]}>{member.role}</Badge>
          {isVa && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRemove}
              disabled={deleteMember.isPending}
              loading={deleteMember.isPending}
            >
              Remove
            </Button>
          )}
        </div>
      </div>

      {isVa && (
        <div className="flex items-center gap-3 flex-wrap mt-2">
          {VA_PERMISSIONS.map((perm) => {
            const granted = member.permissions.includes(perm.key);
            return (
              <label key={perm.key} className="flex items-center gap-1.5 text-xs text-fg-secondary">
                <input
                  type="checkbox"
                  className="size-3.5"
                  checked={granted}
                  disabled={updateMember.isPending}
                  onChange={() => togglePermission(perm.key)}
                />
                {perm.label}
              </label>
            );
          })}
          {member.permissions.length === 0 && (
            <span className="text-xs text-fg-tertiary">No permissions granted yet.</span>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-xs text-negative mt-1">
          {error}
        </p>
      )}
    </div>
  );
}

/** "Add VA" dialog: form → POST → one-time password result view. The
 *  password lives only in this component's state (never logged, never
 *  cached in react-query) and is wiped the moment the dialog closes. */
function AddVaDialog() {
  const createMember = useCreateTeamMember();

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ email: string; oneTimePassword: string } | null>(null);
  const [copied, setCopied] = useState(false);

  function reset() {
    setEmail('');
    setName('');
    setPermissions([]);
    setError(null);
    setResult(null);
    setCopied(false);
  }

  function togglePermission(key: string) {
    setPermissions((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  }

  async function handleSubmit() {
    setError(null);
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    if (!trimmedEmail || !trimmedName) {
      setError('Email and name are required.');
      return;
    }
    try {
      const res = await createMember.mutateAsync({
        email: trimmedEmail,
        name: trimmedName,
        permissions,
      });
      setResult({ email: res.user.email, oneTimePassword: res.oneTimePassword });
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not create this VA.');
    }
  }

  async function handleCopy() {
    if (!result) return;
    await navigator.clipboard.writeText(result.oneTimePassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <Button size="sm" onClick={() => setOpen(true)}>
        Add VA
      </Button>
      <DialogContent className="max-w-sm">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>{result.email} is set up</DialogTitle>
              <DialogDescription>
                Save this password in your password manager now — it will never be shown again.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono bg-surface-elev border border-border-subtle rounded-sm px-3 py-2 truncate">
                {result.oneTimePassword}
              </code>
              <Button size="sm" variant="secondary" onClick={handleCopy}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <DialogFooter>
              <Button size="sm" onClick={() => setOpen(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Add VA</DialogTitle>
              <DialogDescription>
                Creates a VA account with a one-time password you hand off yourself.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.1em] text-fg-tertiary">
                  Email
                </span>
                <Input
                  inputSize="sm"
                  type="email"
                  className="mt-1"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="va@example.com"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.1em] text-fg-tertiary">
                  Name
                </span>
                <Input
                  inputSize="sm"
                  className="mt-1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                />
              </label>
              <div>
                <span className="text-[10px] uppercase tracking-[0.1em] text-fg-tertiary">
                  Permissions
                </span>
                <div className="flex items-center gap-3 flex-wrap mt-1">
                  {VA_PERMISSIONS.map((perm) => (
                    <label
                      key={perm.key}
                      className="flex items-center gap-1.5 text-sm text-fg-secondary"
                    >
                      <input
                        type="checkbox"
                        className="size-4"
                        checked={permissions.includes(perm.key)}
                        onChange={() => togglePermission(perm.key)}
                      />
                      {perm.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            {error && (
              <p role="alert" className="text-xs text-negative mt-3">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                loading={createMember.isPending}
                disabled={createMember.isPending}
              >
                Create
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
